import React from "react";
import UserPlatformStats from "./user-insights-components/userPlatformStats";
import UserLanguageStats from "./user-insights-components/userLanguageStats";
import UserPlatformChart from "./user-insights-components/userPlatformChart";
import ActivityTimeOfDayStats from "./user-insights-components/activityTimeOfDayStats";
import UserOverviewInsights from "../../users/components/userOverviewInsights";

import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

import InfoHover from "modules/common/components/hovers/infoHover";
import Typography from "@mui/material/Typography";
import MainContent from "pages/common/mainContent";
import BasePage from "pages/common/basePage";
import Header from "pages/common/header";
import { usersApi } from "api/instances";
import { FileDownloader } from "utils/fileDownloader";
import { Button } from "@mui/material";

interface UserInsightsPageProps {}

const UserInsightsPage: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [isDownloading, setIsDownloading] = React.useState(false);

    const downloadUserData = async (userIds?: string[]) => {
        setIsDownloading(true);
        const file = {
            name: `User Data-${new Date().toISOString()}.csv`,
        };
        try {
            const url = await usersApi.downloadUserData(userIds || []);
            new FileDownloader(file).downloadProvided(url);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <BasePage fullWidth>
            <Header
                title={
                    <React.Fragment>
                        <span>User Analytics</span>
                    </React.Fragment>
                }
                rightComponent={
                    <React.Fragment>
                        <Button onClick={() => downloadUserData} variant="contained" color="primary" disabled={isDownloading}>
                            Download all user data
                        </Button>
                    </React.Fragment>
                }
            />
            <MainContent>
                <div className="user-insights-container">
                    <div className="row-insights">
                        <div className="overview-insights">
                            <div className="user-insights-wrapper">
                                <Typography variant="h2">
                                    Key Insights
                                    <InfoHover>
                                        <div>Total users counts all the accounts in your Sparrow directory.</div>
                                        <br />
                                        <div>
                                            Deactivated users are accounts who can not momentarily access the platform. You can activate
                                            these users to return their access.
                                        </div>
                                        <br />
                                        <div>
                                            Engaged users have logged in to the platform and viewed published content within the last 30
                                            days.
                                        </div>
                                        <br />
                                        <div>
                                            Social users count Google, LinkedIn, Facebook, Apple and Microsoft Personal accounts. Pending
                                            social user invites are social users who have not logged in and activated their accounts yet.
                                        </div>
                                    </InfoHover>
                                </Typography>
                                <UserOverviewInsights />
                            </div>
                        </div>
                        <div className="insights-account-types">
                            <div className="user-insights-wrapper">
                                <Typography variant="h2">User Account Types</Typography>
                                <div className="insights-platforms">
                                    <UserPlatformChart />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row-insights">
                        <div className="user-insights-wrapper">
                            <Typography variant="h2">User Preferences</Typography>
                            <UserLanguageStats />
                            <UserPlatformStats />
                        </div>
                    </div>
                    <div className="row-insights">
                        <div className="user-insights-wrapper">
                            <Typography variant="h2">Behavior</Typography>
                            <ActivityTimeOfDayStats timeZone={props.tenantSettings.timeZone} />
                        </div>
                    </div>
                </div>
            </MainContent>
        </BasePage>
    );
};

const connector = connect(
    (state: GlobalApplicationState, ownProps: UserInsightsPageProps) => ({
        ...ownProps,
        tenantSettings: state.settings.tenantSettings,
        users: state.users.users,
    }),
    {}
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(UserInsightsPage);
